import React, { useMemo } from "react"
import styled from "styled-components"
import Text from "@/components/common/Text"
import { useNavigatorContext } from "@/components/BranchNavigator"
import Unit, { Skeleton as UnitSkeleton } from "@/components/BranchNavigator/Units/Unit"
import { getCssSizeValue, getMapKey } from "@/utils/utils"
import GridContainer from "@/components/common/GridContainer"
import { BREAKPOINTS } from "@/styles/themeSettings"
import useCurrentBranchData from "@/hooks/useCurrentBranchData"
import { UNIT_CATEGORY } from "@/constants"
import { useIsMobile } from "@/hooks/useDevice"
import useGlobals from "@/hooks/useGlobals"

const ROW_GAP = 32

const Wrapper = styled.div`
	//min-height: 80svh;
	width: 100%;
	display: flex;
	flex-direction: column;
	row-gap: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
	margin-top: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-top: unset;
		row-gap: ${getCssSizeValue(ROW_GAP, BREAKPOINTS.DESKTOP)};
	}
`

const Category = styled(GridContainer)`
	grid-template-columns: repeat(var(--grid-count), 1fr) !important;
	row-gap: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-bottom: ${getCssSizeValue(80 - ROW_GAP, BREAKPOINTS.DESKTOP)};
		row-gap: var(--grid-gap);
		&.grid-12 {
			grid-template-columns: repeat(12, 1fr) !important;
		}
	}
`
const CategoryName = styled(Text)`
	background-color: ${({ theme }) => theme.colors.periwinkle};
	width: 100%;
	text-align: center;
	padding: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)} 0;

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: unset;
		background-color: unset;
		width: fit-content;
		text-align: unset;
		margin-left: var(--local-padding-x);
	}
`

const Units = ({ className = "", ...props }) => {
	const { units, isLoading } = useNavigatorContext()
	const currentBranch = useCurrentBranchData()
	const isMobile = useIsMobile()
	const unitCategoryRegional = useGlobals("unitCategoryRegional")
	const unitCategoryDelegations = useGlobals("unitCategoryDelegations")
	const unitCategoryHome = useGlobals("unitCategoryHome")

	const TRANSLATIONS = {
		[UNIT_CATEGORY.REGIONAL]: unitCategoryRegional,
		[UNIT_CATEGORY.DELEGATIONS]: unitCategoryDelegations,
		[UNIT_CATEGORY.HOME]: unitCategoryHome,
	}
	// eslint-disable-next-line no-unused-vars
	const [_, categorizedUnits] = useMemo(() => {
		const general = units.find((item) => item.state === currentBranch.id)
		if (!general) return [{}, []]
		const categorized = [...new Set(general.items.map((item) => item.category))]
			.map((category) => {
				const items = general.items.filter((item) => item.category === category)
				return {
					category,
					items,
				}
			})
			.sort((a, b) => {
				// if a is 'regional', it should come first
				if (a.category === UNIT_CATEGORY.REGIONAL) return -1
				// if b is 'regional', it should come first
				if (b.category === UNIT_CATEGORY.REGIONAL) return 1
				// if neither is 'regional', they are equal
				return 0
			})
		return [general, categorized]
	}, [units, currentBranch])

	if (isLoading) {
		return (
			<Wrapper className={`${className}`} {...props}>
				<Category className={`${className} grid-12 no-margin`}>
					{[...new Array(4)].map((unit, index) => {
						const hasBorderRight = (index + 1) % 4 !== 0
						return <UnitSkeleton key={getMapKey(unit, index)} borderRight={hasBorderRight} />
					})}
				</Category>
			</Wrapper>
		)
	}

	return (
		<Wrapper className={`${className}`} {...props}>
			{categorizedUnits.length > 0 &&
				categorizedUnits.map((group, key) => (
					<React.Fragment key={getMapKey({ id: group.category }, key)}>
						<CategoryName className=" col-desktop-12 typo-borna-19">{TRANSLATIONS?.[group.category] || ""}</CategoryName>
						<Category className={`${className} grid-12 no-margin ${isMobile ? "padding" : ""}`}>
							{group.items.map((unit, index) => {
								const hasBorderRight = (index + 1) % 4 !== 0
								return <Unit data={unit} key={getMapKey(unit, index)} borderRight={hasBorderRight} />
							})}
						</Category>
					</React.Fragment>
				))}
		</Wrapper>
	)
}

export default Units
